import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';

export interface LegacyCommonAreaServiceRequest {
  id: string;
  ticketNumber?: string;
  suiteName?: string;
  stratalot?: string | null;
  description?: string;
  area?: { name?: string } | string | null;
  itemName?: string;
  requestType?: string | null;
  status?: string;
  customLabel?: string | null;
  createdDate?: string;
  serviceProvider?: string | null;
  scheduleDatest?: string | null;
  tarionItemNumber?: string | null;
  groupId?: string | null;
  groupName?: string | null;
  attachment?: string | null;
  hasAttachment?: boolean;
  firstCompressedAttachment?: string | null;
  totalAttachments?: number;
  propertyName?: string | null;
  commonAreaName?: string | null;
}

export interface LegacyCommonAreaServiceRequestEnvelope {
  serviceRequestsList?: {
    results?: LegacyCommonAreaServiceRequest[];
    pageCount?: number;
  };
}

/* legacy/workaround: legacy BOS endpoint returns `area` as either a string or
 * an object envelope. Flatten to a single string for the dashboard table. */
export const flattenLegacyArea = (
  area: LegacyCommonAreaServiceRequest['area']
): string => {
  if (typeof area === 'string') {
    return area;
  }

  if (typeof area === 'object' && area !== null) {
    return area.name ?? '';
  }

  return '';
};

export const normalizeCommonAreaServiceRequest = (
  legacy: LegacyCommonAreaServiceRequest
): IServiceRequest => {
  const id: string = legacy.id;
  const number: string = legacy.suiteName ?? legacy.ticketNumber ?? '';
  const ticketNumber: string = legacy.ticketNumber ?? '';
  const stratalot: string = legacy.stratalot ?? '';
  const description: string = legacy.description ?? '';
  const area: string = flattenLegacyArea(legacy.area);
  const item: string = legacy.itemName ?? '';
  const itemName: string = legacy.itemName ?? '';
  const attachment: string = legacy.attachment ?? '';
  const type: string = legacy.requestType ?? '';
  const status: string = legacy.status ?? '';
  const customLabel: string | null = legacy.customLabel ?? null;

  const createdDate: string = legacy.createdDate ?? '';

  const serviceProvider: string | null = legacy.serviceProvider ?? null;
  const scheduledAt: string | null = legacy.scheduleDatest ?? null;
  const tarionItemNumber: string | null = legacy.tarionItemNumber ?? null;
  const groupId: string | null = legacy.groupId ?? null;
  const groupName: string | null = legacy.groupName ?? null;
  const propertyName: string | null = legacy.propertyName ?? null;

  /* legacy/workaround: legacy endpoint omits these on rows with no
   * attachments. Force-coerce so the dashboard table's <Attachments> cell
   * never receives undefined (which would crash the row). */
  const hasAttachment: boolean = Boolean(legacy.hasAttachment);
  const firstCompressedAttachment: string | null =
    legacy.firstCompressedAttachment ?? null;
  const totalAttachments: number = legacy.totalAttachments ?? 0;

  const normalized: IServiceRequest = {
    id,
    number,
    ticketNumber,
    stratalot,
    description,
    area,
    item,
    itemName,
    attachment,
    type,
    status,
    customLabel,
    createdDate,
    serviceProvider,
    scheduledAt,
    tarionItemNumber,
    groupId,
    groupName,
    propertyName,
    hasAttachment,
    firstCompressedAttachment,
    totalAttachments,
  } as unknown as IServiceRequest;

  return normalized;
};
